import { useEffect } from 'react';

/**
 * Global IntersectionObserver that adds `.is-visible` to any element with
 * a `data-reveal` attribute the first time it enters the viewport. Also
 * unwraps .mask-headline children in one clean pass.
 *
 * Mounted once (from App.js). Respects prefers-reduced-motion by simply
 * marking every element visible immediately.
 */
export default function useRevealObserver() {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const targets = () => Array.from(document.querySelectorAll('[data-reveal], .mask-headline'));

    if (reduce) {
      targets().forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    // Initial scan
    targets().forEach(el => observer.observe(el));

    // Re-scan after route changes / late-mounted content via MutationObserver
    const mo = new MutationObserver(() => {
      targets().forEach(el => {
        if (!el.classList.contains('is-visible')) observer.observe(el);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mo.disconnect();
    };
  }, []);
}
